import React, { useEffect, useState, useCallback } from 'react';
import { IoMdClose } from 'react-icons/io';
import dayjs from 'dayjs';
import axios from 'axios';
import { toast } from 'react-toastify';
import './ActiveSessionsModal.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getToken = () => {
    try {
        const raw = sessionStorage.getItem('tokens') || localStorage.getItem('tokens');
        if (!raw) return '';
        const parsed = JSON.parse(raw);
        return parsed?.token || '';
    } catch {
        return '';
    }
};

// device_fingerprint is already a human-readable label built at login time (see
// describeThisDevice() in AuthReducer/action.js) — shown as-is. Older/foreign sessions
// (mobile app, or a session predating this label format) just fall back to the raw value.
const describeDevice = (fingerprint) => {
    if (!fingerprint) return 'Unknown device';
    return fingerprint.length > 60 ? `${fingerprint.slice(0, 60)}…` : fingerprint;
};

const formatDate = (value) => (value ? dayjs(value).format('MMM DD, YYYY HH:mm') : '-');

const ActiveSessionsModal = ({ closeModal }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [revokingId, setRevokingId] = useState(null);
    const [revokingOthers, setRevokingOthers] = useState(false);

    const fetchSessions = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setError('Authentication token not found. Please login again.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`${API_BASE}/officer/sessions`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSessions(res.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load active sessions.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    const handleRevoke = async (session_id) => {
        const token = getToken();
        if (!token) return;
        setRevokingId(session_id);
        try {
            await axios.post(`${API_BASE}/officer/sessions/revoke`, { session_id }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Session revoked');
            setSessions((prev) => prev.filter((s) => s.session_id !== session_id));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to revoke session');
        } finally {
            setRevokingId(null);
        }
    };

    const handleRevokeOthers = async () => {
        const token = getToken();
        if (!token) return;
        setRevokingOthers(true);
        try {
            const res = await axios.post(`${API_BASE}/officer/sessions/revoke-others`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const count = res.data?.data?.revoked_count ?? 0;
            toast.success(count > 0 ? `Logged out of ${count} other device(s)` : 'No other devices to log out of');
            setSessions((prev) => prev.filter((s) => s.is_current));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to log out other devices');
        } finally {
            setRevokingOthers(false);
        }
    };

    const hasOtherSessions = sessions.some((s) => !s.is_current);

    return (
        <div>
            <div className="modal-overlay" onClick={closeModal}></div>
            <div className="add-user-modal active-sessions-modal" role="dialog" aria-modal="true">
                <div className="modal-header">
                    <h5>Active Sessions</h5>
                    <button className="close-btn" onClick={closeModal} aria-label="Close">
                        <IoMdClose />
                    </button>
                </div>

                <p className="modal-subtitle">
                    Devices currently signed in to your account.
                </p>

                {error && <div className="error-message">{error}</div>}

                {loading ? (
                    <div className="sessions-empty-state">Loading sessions…</div>
                ) : sessions.length === 0 ? (
                    <div className="sessions-empty-state">No active sessions found.</div>
                ) : (
                    <div className="sessions-list">
                        {sessions.map((s) => (
                            <div key={s.session_id} className={`session-row ${s.is_current ? 'session-row-current' : ''}`}>
                                <div className="session-row-main">
                                    <div className="session-device">
                                        {describeDevice(s.device_fingerprint)}
                                        {s.is_current && <span className="session-current-badge">This device</span>}
                                    </div>
                                    <div className="session-meta">
                                        {s.ip_address ? `${s.ip_address} · ` : ''}Last active {formatDate(s.last_active_at)}
                                    </div>
                                    <div className="session-meta">
                                        Signed in {formatDate(s.created_at)}
                                    </div>
                                </div>
                                {!s.is_current && (
                                    <button
                                        type="button"
                                        className="session-revoke-btn"
                                        disabled={revokingId === s.session_id}
                                        onClick={() => handleRevoke(s.session_id)}
                                    >
                                        {revokingId === s.session_id ? 'Revoking…' : 'Revoke'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="modal-actions">
                    <button
                        type="button"
                        className="add-user-btn sessions-revoke-others-btn"
                        disabled={!hasOtherSessions || revokingOthers}
                        onClick={handleRevokeOthers}
                    >
                        {revokingOthers ? 'Logging out…' : 'Log out of all other devices'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ActiveSessionsModal;
